import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Contact,
  CreateContactInput,
  UpdateContactInput,
} from '@accounting/shared';
import { api } from '../lib/api';

export const contactsKey = (
  companyId: string | null,
  opts: { includeInactive?: boolean; contactType?: string } = {},
) =>
  [
    'contacts',
    companyId ?? 'none',
    opts.includeInactive ? 'all' : 'active',
    opts.contactType ?? 'any',
  ] as const;

export function useContacts(
  companyId: string | null,
  opts: { includeInactive?: boolean; contactType?: string } = {},
) {
  return useQuery({
    queryKey: contactsKey(companyId, opts),
    enabled: companyId !== null,
    queryFn: () => {
      const params = new URLSearchParams();
      if (opts.includeInactive) params.set('includeInactive', 'true');
      if (opts.contactType) params.set('contactType', opts.contactType);
      const qs = params.toString();
      return api
        .get<{ contacts: Contact[] }>(
          `/companies/${companyId}/contacts${qs ? '?' + qs : ''}`,
        )
        .then((r) => r.contacts);
    },
  });
}

export function useCreateContact(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContactInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ contact: Contact }>(`/companies/${companyId}/contacts`, input)
        .then((r) => r.contact);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts', companyId ?? 'none'] });
    },
  });
}

export function useUpdateContact(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateContactInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ contact: Contact }>(
          `/companies/${companyId}/contacts/${id}`,
          input,
        )
        .then((r) => r.contact);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts', companyId ?? 'none'] });
    },
  });
}

export function useDeactivateContact(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api
        .delete<{ contact: Contact }>(`/companies/${companyId}/contacts/${id}`)
        .then((r) => r.contact);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts', companyId ?? 'none'] });
    },
  });
}
